package fr.frux.shared.data.mapper

import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.ObservableTreeStatus
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeCorrectionMark
import fr.frux.shared.domain.model.TreeObservation
import fr.frux.shared.domain.model.TreePhoto
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.model.TreeTrait
import fr.frux.shared.domain.model.TreeTraitKind
import fr.frux.shared.dto.CertaintyDto
import fr.frux.shared.dto.TreeDto
import fr.frux.shared.dto.TreePhotoDto
import fr.frux.shared.dto.TreeStatusDto
import fr.frux.shared.dto.TreeStatusObservationDto
import fr.frux.shared.dto.TreeTraitKindDto
import fr.frux.shared.dto.TreeTraitSummary

/**
 * Traduction transport ↔ domaine (#109).
 *
 * Vit dans la couche data : le domaine ne doit connaître ni les DTO ni le module
 * `backend-shared` qui les porte — sinon un changement de contrat API se propage
 * mécaniquement jusqu'aux entités métier.
 */
fun TreeDto.toDomain(): Tree =
    Tree(
        id = id ?: 0,
        species = species.toDomain(),
        latitude = latitude,
        longitude = longitude,
        status = status.toDomain(),
        description = description,
        photoUrl = photoUrl,
        // Dé-nullification côté mapper : le transport laisse `photos` null hors du détail, le
        // domaine veut toujours une liste (vide si non renseignée).
        photos = photos?.map { it.toDomain() }.orEmpty(),
        createdBy = createdBy,
        createdByUsername = createdByUsername,
        createdAt = createdAt?.toEpochMilliseconds() ?: 0,
        updatedAt = updatedAt?.toEpochMilliseconds() ?: 0,
        traits = traits?.map { it.toDomain() }.orEmpty(),
        // Statuts non observables (`UNAVAILABLE`/`UNKNOWN`) : impossible en pratique par contrainte
        // DB, mais mappage défensif — l'observation est ignorée plutôt que de propager un statut
        // que le domaine ne sait pas représenter.
        latestObservation = latestObservation?.toDomain(),
        lastObservedAt = lastObservedAt,
        myObservationStatus = myObservationStatus?.toObservableOrNull(),
        harvestPart = harvestPart?.toDomain(),
        certainty = certainty.toDomain(),
        isFavorite = isFavorite,
        disputed = disputed,
        disputedKinds = disputedKinds,
        reportedByMe = reportedByMe,
        reportedGoneByMe = reportedGoneByMe,
        siteId = siteId,
        lastCorrection = lastCorrection?.let { TreeCorrectionMark(it.authorName, it.createdAt.toEpochMilliseconds()) },
    )

fun TreePhotoDto.toDomain(): TreePhoto =
    TreePhoto(
        id = id,
        fullUrl = fullUrl,
        thumbUrl = thumbUrl,
        width = width,
        height = height,
        authorId = authorId,
        createdAt = createdAt.toEpochMilliseconds(),
    )

fun TreeStatusDto.toDomain(): TreeStatus =
    when (this) {
        TreeStatusDto.FLOWERING -> TreeStatus.FLOWERING
        TreeStatusDto.RIPE_READY -> TreeStatus.RIPE_READY
        TreeStatusDto.PICKED_CLEAN -> TreeStatus.PICKED_CLEAN
        TreeStatusDto.UNAVAILABLE -> TreeStatus.UNAVAILABLE
        TreeStatusDto.UNKNOWN -> TreeStatus.UNKNOWN
    }

fun TreeStatus.toDto(): TreeStatusDto =
    when (this) {
        TreeStatus.FLOWERING -> TreeStatusDto.FLOWERING
        TreeStatus.RIPE_READY -> TreeStatusDto.RIPE_READY
        TreeStatus.PICKED_CLEAN -> TreeStatusDto.PICKED_CLEAN
        TreeStatus.UNAVAILABLE -> TreeStatusDto.UNAVAILABLE
        TreeStatus.UNKNOWN -> TreeStatusDto.UNKNOWN
    }

/** `null` pour `UNAVAILABLE`/`UNKNOWN` : ces statuts n'existent pas dans le domaine observable. */
fun TreeStatusDto.toObservableOrNull(): ObservableTreeStatus? =
    when (this) {
        TreeStatusDto.FLOWERING -> ObservableTreeStatus.FLOWERING
        TreeStatusDto.RIPE_READY -> ObservableTreeStatus.RIPE_READY
        TreeStatusDto.PICKED_CLEAN -> ObservableTreeStatus.PICKED_CLEAN
        TreeStatusDto.UNAVAILABLE, TreeStatusDto.UNKNOWN -> null
    }

fun ObservableTreeStatus.toDto(): TreeStatusDto =
    when (this) {
        ObservableTreeStatus.FLOWERING -> TreeStatusDto.FLOWERING
        ObservableTreeStatus.RIPE_READY -> TreeStatusDto.RIPE_READY
        ObservableTreeStatus.PICKED_CLEAN -> TreeStatusDto.PICKED_CLEAN
    }

fun TreeTraitKindDto.toDomain(): TreeTraitKind =
    when (this) {
        TreeTraitKindDto.ACCESS_DIFFICULT -> TreeTraitKind.ACCESS_DIFFICULT
        TreeTraitKindDto.DELICIOUS -> TreeTraitKind.DELICIOUS
    }

fun TreeTraitKind.toDto(): TreeTraitKindDto =
    when (this) {
        TreeTraitKind.ACCESS_DIFFICULT -> TreeTraitKindDto.ACCESS_DIFFICULT
        TreeTraitKind.DELICIOUS -> TreeTraitKindDto.DELICIOUS
    }

fun TreeTraitSummary.toDomain(): TreeTrait = TreeTrait(kind = kind.toDomain(), count = count, mine = mine)

/**
 * `null` si le statut observé n'est pas observable (défensif, cf. [Tree.latestObservation]) :
 * l'observation entière est ignorée plutôt que de porter un statut non représentable.
 */
fun TreeStatusObservationDto.toDomain(): TreeObservation? =
    status.toObservableOrNull()?.let {
        TreeObservation(
            status = it,
            observedAtMillis = observedAt.toEpochMilliseconds(),
            observerUsername = observerUsername,
        )
    }

fun CertaintyDto.toDomain(): Certainty =
    when (this) {
        CertaintyDto.CERTAIN -> Certainty.CERTAIN
        CertaintyDto.PROBABLE -> Certainty.PROBABLE
        CertaintyDto.TO_CONFIRM -> Certainty.TO_CONFIRM
    }

fun Certainty.toDto(): CertaintyDto =
    when (this) {
        Certainty.CERTAIN -> CertaintyDto.CERTAIN
        Certainty.PROBABLE -> CertaintyDto.PROBABLE
        Certainty.TO_CONFIRM -> CertaintyDto.TO_CONFIRM
    }
